import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { foundSelector, promptSelector } from 'store/reducers/nlp/nlp-reducer';
import {
  alternativesDisabledSelector,
  filtersSelector,
  pushPeople,
  setPeople,
  setDefaultPeople,
  setShouldFetch,
  setShouldGetMorePeople,
  setUuids,
  setDefaultUuids,
  shouldFetchSelector,
  shouldGetMorePeopleSelector,
  uuidsSelector,
  currentPageSelector,
  setCurrentPage,
  IPeopleFilters,
  setIsPeopleLoading,
  setIsMorePeopleLoading,
  exclusiveFiltersSelector,
  allCurrentFiltersSelector,
  sortSelector,
  isPeopleLoadingSelector,
  sortedUuidsSelector,
  setSort,
  setPeopleError,
  emptyAllFilters,
  PEOPLE_CONTEXT,
  setDetails,
  IPeopleContext,
  setOriginalPeople,
  originalPeopleSelector,
  selectionFiltersSelector,
  setCurrentStep,
  currentStepSelector,
  groupedFiltersSelector,
  filteredUuidsSelector,
  scopeSelector,
  accountHoldersSelector,
  PEOPLE_FILTER_TYPE,
  samplesPerPageSelector,
} from 'store/reducers/people/people-reducer';
import { shouldDisableAlternativesForUuids } from 'utils/people-alternatives';

import usePeople from './usePeople';
import usePeopleSamples from './usePeopleSamples';

export const SAMPLES_PER_PAGE = 17;

export default function usePeopleContent({ context }: { context: IPeopleContext }) {
  const dispatch = useDispatch();
  const samplesPerPage = useSelector(samplesPerPageSelector) || SAMPLES_PER_PAGE;
  const scope = useSelector(scopeSelector);
  const prompt = useSelector(promptSelector);
  const filters = useSelector(filtersSelector);
  const exclusiveFilters = useSelector(exclusiveFiltersSelector);
  const shouldFetch = useSelector(shouldFetchSelector);
  const shouldGetMorePeople = useSelector(shouldGetMorePeopleSelector);
  const currentPage = useSelector(currentPageSelector);
  const uuids = useSelector(uuidsSelector);
  const allCurrentFilters = useSelector(allCurrentFiltersSelector);
  const sort = useSelector(sortSelector);
  const sortedUuids = useSelector(sortedUuidsSelector);
  const isPeopleLoading = useSelector(isPeopleLoadingSelector);
  const selectedCriteria = useSelector(selectionFiltersSelector);
  const originalPeople = useSelector(originalPeopleSelector);
  const found = useSelector(foundSelector);
  const currentStep = useSelector(currentStepSelector);
  const accountHolders = useSelector(accountHoldersSelector);
  const alternativesDisabled = useSelector(alternativesDisabledSelector);
  const groupedFilters = useSelector<any>(groupedFiltersSelector);
  const filteredUuids = useSelector(filteredUuidsSelector);
  const [uuidsToFetch, setUuidsToFetch] = useState<string[]>([]);
  const [currentFilters, setCurrentFilters] = useState<
    IPeopleFilters & { groupings?: { uuid: string; type: string }[] }
  >(
    Object.keys(allCurrentFilters).reduce((acc, key) => {
      if (key !== PEOPLE_FILTER_TYPE.ACCOUNT_HOLDERS) {
        acc[key] = allCurrentFilters[key];
      }
      return acc;
    }, {})
  );
  const getGroupings = useCallback(() => {
    const groupings = Object.values(groupedFilters)
      .filter((group) => {
        return group[0].datatype !== PEOPLE_FILTER_TYPE.ACCOUNT_HOLDERS;
      })
      .map((group, index) => {
        const groupName = Object.keys(groupedFilters)[index];

        return group.map((item) => ({
          uuid: item.uuid,
          type: item.datatype,
          group_name:
            ['clients', 'industries', 'companies'].includes(item.datatype) && accountHolders
              ? `Account holders for ${groupName}`
              : groupName,
        }));
      });

    return groupings;
  }, [groupedFilters, accountHolders]);

  const peopleQuery = usePeople(
    currentFilters,
    samplesPerPage,
    context === PEOPLE_CONTEXT.TEAMING,
    scope,
    allCurrentFilters[PEOPLE_FILTER_TYPE.ACCOUNT_HOLDERS],
    {
      onSettled: (data, error) => {
        // if current filters are empty
        if (
          (Object.values(currentFilters).every((f) => f && f.length === 0) ||
            allCurrentFilters[PEOPLE_FILTER_TYPE.ACCOUNT_HOLDERS]) &&
          scope.type !== null &&
          scope.uuid !== null &&
          prompt === null
        ) {
          dispatch(setUuids(data?.collection));
          dispatch(setDefaultUuids(data?.collection));
        }
      },
    }
  );
  const { data, isLoading, isRefetching, isError, isRefetchError } = peopleQuery;
  const {
    data: samples,
    isLoading: isMorePeopleLoading,
    refetch: refetchSamples,
    isRefetching: isSamplesRefetching,
  } = usePeopleSamples(uuidsToFetch, { enabled: uuidsToFetch.length > 0 });

  useEffect(() => {
    dispatch(setPeopleError(isError || isRefetchError));
  }, [isError, isRefetchError]);

  useEffect(() => {
    if (shouldFetch) {
      const groupings = getGroupings();
      setCurrentFilters({ ...allCurrentFilters, groupings });
      dispatch(setSort('relevance'));
      dispatch(setShouldFetch(false));
    }
  }, [filters, shouldFetch, allCurrentFilters]);

  useEffect(() => {
    dispatch(setCurrentPage(1));
    setUuidsToFetch([]);
    dispatch(setSort('relevance'));
  }, [currentFilters]);

  /**
   * Reset list when all filters are cleared
   */
  useEffect(() => {
    if (Object.values(filters).every((f) => f.length === 0) && !shouldFetch) {
      if (!found || !found.people) {
        dispatch(emptyAllFilters({}));
        dispatch(setShouldFetch(true));
      }
    }
  }, [filters, found]);

  useEffect(() => {
    if (exclusiveFilters) {
      const groupings = getGroupings();
      setCurrentFilters({ ...allCurrentFilters, groupings });
    }
  }, [exclusiveFilters]);

  useEffect(() => {
    dispatch(setIsPeopleLoading(isLoading || isRefetching));
  }, [isLoading, data, isRefetching]);

  useEffect(() => {
    dispatch(setIsMorePeopleLoading(isMorePeopleLoading || isSamplesRefetching));
    if (isPeopleLoading && !isMorePeopleLoading && !isSamplesRefetching && data) {
      dispatch(setIsPeopleLoading(false));
    }
  }, [isMorePeopleLoading, samples, isSamplesRefetching, data]);

  useEffect(() => {
    if (data && !isRefetching) {
      const shouldSkipAlternatives = shouldDisableAlternativesForUuids(data.collection);

      dispatch(setUuids(data.collection));
      dispatch(setPeople(data.samples));
      dispatch(setOriginalPeople(data.samples));
      dispatch(setDefaultPeople(data.samples));
      if (data.details) {
        dispatch(setDetails(data.details));
      }
      if (
        (Object.keys(allCurrentFilters).some((key) => allCurrentFilters[key].length > 0) ||
          allCurrentFilters[PEOPLE_FILTER_TYPE.ACCOUNT_HOLDERS]) &&
        currentStep === 1
      ) {
        dispatch(setCurrentStep(shouldSkipAlternatives ? 4 : currentStep + 1));
      }
    }
  }, [data, isRefetching]);

  useEffect(() => {
    if (alternativesDisabled && [2, 3].includes(currentStep)) {
      dispatch(setCurrentStep(4));
    }
  }, [alternativesDisabled, currentStep]);

  useEffect(() => {
    if (selectedCriteria === null) {
      dispatch(setPeople(originalPeople));
    }
  }, [selectedCriteria]);

  useEffect(() => {
    if (samples && samples?.people.length > 0 && !isSamplesRefetching) {
      dispatch(pushPeople(samples.people));
    }
  }, [samples, isSamplesRefetching]);

  useEffect(() => {
    if (shouldGetMorePeople && currentStep === 4) {
      setUuidsToFetch(
        filteredUuids
          .map((u) => u.uuid)
          .slice(currentPage * samplesPerPage, (currentPage + 1) * samplesPerPage)
      );
      dispatch(setCurrentPage(currentPage + 1));
      dispatch(setShouldGetMorePeople(false));
    }
  }, [shouldGetMorePeople, currentStep]);

  useEffect(() => {
    if (sort === 'relevance') {
      peopleQuery.refetch();
    } else if (sort === 'name') {
      dispatch(setIsPeopleLoading(true));
      dispatch(setUuids(sortedUuids));
      dispatch(setPeople([]));
      dispatch(setCurrentPage(1));
      const sortedUuidsToFetch = sortedUuids.slice(0, samplesPerPage).map((u) => u.uuid);
      if (sortedUuidsToFetch.every((uuid) => uuidsToFetch.includes(uuid))) {
        refetchSamples();
      } else {
        setUuidsToFetch(sortedUuidsToFetch);
      }
    }
  }, [sort]);

  useEffect(() => {
    if (found && Object.keys(found).length === 1 && found.people && found.people.length > 0) {
      dispatch(setPeople([]));
    }
  }, [found]);
}
